import { useState, useEffect, useCallback, useMemo } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailRouter } from "../lib/router-context";
import { Bot, Store, Search, ChevronDown, ChevronUp, X, ZoomIn, List, LayoutGrid, LifeBuoy } from "lucide-react";

interface PuduRobot {
  robot_sn: string;
  robot_mac: string | null;
  robot_type: string | null;
  robot_name: string | null;
  product_code: string | null;
  product_name: string | null;
  shop_name: string | null;
  image_url: string | null;
  last_sync_at: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "hier";
  if (days < 30) return `il y a ${days} jour${days > 1 ? "s" : ""}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `il y a ${months} mois`;
  return new Date(iso).toLocaleDateString("fr-FR");
}

interface LightboxState {
  imageUrl: string;
  label: string;
}

type SortKeyR = "nom" | "magasin" | "synchro";
type SortDirR = "asc" | "desc";

function SortThR({ label, sortK, currentKey, currentDir, onToggle, right }: {
  label: string; sortK: SortKeyR; currentKey: SortKeyR; currentDir: SortDirR;
  onToggle: (k: SortKeyR) => void; right?: boolean;
}) {
  const active = currentKey === sortK;
  return (
    <th
      className={`px-3 py-2.5 text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none whitespace-nowrap ${right ? "text-right" : "text-left"}`}
      onClick={() => onToggle(sortK)}
    >
      <span className={`inline-flex items-center gap-1 ${right ? "flex-row-reverse" : ""}`}>
        {label}
        {active
          ? (currentDir === "asc" ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />)
          : <ChevronDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  );
}

function RobotListRow({ robot, onLightbox, onOpenTicket }: {
  robot: PuduRobot;
  onLightbox: (url: string, label: string) => void;
  onOpenTicket: (robot: PuduRobot) => void;
}) {
  const label = robot.product_name || robot.robot_type || robot.product_code || "Robot Pudu";
  return (
    <tr className="hover:bg-secondary/30 transition-colors border-b border-border/60" data-testid={`row-robot-${robot.robot_sn}`}>
      {/* Photo */}
      <td className="px-4 py-2.5">
        <div
          className={`w-12 h-12 rounded-lg bg-muted/30 border border-border flex items-center justify-center overflow-hidden ${robot.image_url ? "cursor-zoom-in" : ""}`}
          onClick={() => robot.image_url && onLightbox(robot.image_url, label)}
        >
          {robot.image_url
            ? <img src={robot.image_url} alt={label} className="w-full h-full object-contain p-1" />
            : <Bot className="w-5 h-5 text-muted-foreground/40" />}
        </div>
      </td>
      {/* Nom modèle */}
      <td className="px-3 py-2.5">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {robot.product_code && (
          <span className="text-[10px] font-mono text-muted-foreground">{robot.product_code}</span>
        )}
      </td>
      {/* SN */}
      <td className="px-3 py-2.5">
        <span className="inline-flex items-center text-[11px] font-mono bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-800 whitespace-nowrap">
          {robot.robot_sn}
        </span>
      </td>
      {/* MAC */}
      <td className="px-3 py-2.5">
        {robot.robot_mac
          ? <span className="text-[11px] font-mono bg-muted text-muted-foreground px-2 py-0.5 rounded-full border border-border">{robot.robot_mac}</span>
          : <span className="text-muted-foreground">—</span>}
      </td>
      {/* Magasin */}
      <td className="px-3 py-2.5">
        {robot.shop_name
          ? <div className="flex items-center gap-1.5 text-sm text-foreground"><Store className="w-3.5 h-3.5 text-muted-foreground shrink-0" />{robot.shop_name}</div>
          : <span className="text-muted-foreground">—</span>}
      </td>
      {/* Dernière synchro */}
      <td className="px-4 py-2.5 text-sm text-muted-foreground whitespace-nowrap">
        {formatRelative(robot.last_sync_at)}
      </td>
      {/* Action */}
      <td className="px-4 py-2.5">
        <button
          onClick={() => onOpenTicket(robot)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors whitespace-nowrap"
          data-testid={`button-open-ticket-${robot.robot_sn}`}
        >
          <LifeBuoy className="w-3.5 h-3.5" />
          Ouvrir un ticket
        </button>
      </td>
    </tr>
  );
}

export default function RobotsActifsPage() {
  const { navigate } = usePortailRouter();
  const [robots, setRobots] = useState<PuduRobot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("tous");
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  // Vue liste / grille avec persistence localStorage
  const [viewModePref, setViewModePref] = useState<"list" | "grid">(() => {
    const saved = localStorage.getItem("portail_robots_viewMode");
    return saved === "list" || saved === "grid" ? saved : "grid";
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const viewMode = isMobile ? "grid" : viewModePref;
  const setViewMode = (v: "list" | "grid") => {
    setViewModePref(v);
    localStorage.setItem("portail_robots_viewMode", v);
  };

  // Tri (vue liste uniquement)
  const [sortKey, setSortKey] = useState<SortKeyR>("nom");
  const [sortDir, setSortDir] = useState<SortDirR>("asc");
  const toggleSort = (k: SortKeyR) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const openLightbox = useCallback((imageUrl: string, label: string) => {
    setLightbox({ imageUrl, label });
  }, []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  const handleOpenTicket = useCallback((robot: PuduRobot) => {
    const modele = modelLabel(robot);
    window.history.pushState(
      { openTicketFor: { sn: robot.robot_sn, clientFinal: robot.shop_name ?? "", modele } },
      "",
      "/tickets"
    );
    navigate("/tickets");
  }, [navigate]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeLightbox(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, closeLightbox]);

  useEffect(() => {
    authFetch("/api/portail/robots")
      .then(r => {
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then(setRobots)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const modelLabel = (r: PuduRobot) => r.product_name || r.robot_type || r.product_code || "Robot Pudu";

  const robotTypes = Array.from(
    new Set(robots.map(r => r.product_code || r.robot_type).filter(Boolean) as string[])
  ).sort();

  const filtered = useMemo(() => robots.filter(r => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (r.robot_sn ?? "").toLowerCase().includes(q) ||
      (r.robot_type ?? "").toLowerCase().includes(q) ||
      (r.robot_name ?? "").toLowerCase().includes(q) ||
      (r.product_name ?? "").toLowerCase().includes(q) ||
      (r.product_code ?? "").toLowerCase().includes(q) ||
      (r.shop_name ?? "").toLowerCase().includes(q);
    const matchType = typeFilter === "tous" || r.product_code === typeFilter || r.robot_type === typeFilter;
    return matchSearch && matchType;
  }), [robots, search, typeFilter]);

  const sortedFiltered = useMemo(() => {
    if (viewMode !== "list") return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      if (sortKey === "nom") { va = modelLabel(a); vb = modelLabel(b); }
      else if (sortKey === "magasin") { va = a.shop_name ?? ""; vb = b.shop_name ?? ""; }
      else {
        va = a.last_sync_at ? new Date(a.last_sync_at).getTime() : 0;
        vb = b.last_sync_at ? new Date(b.last_sync_at).getTime() : 0;
      }
      if (typeof va === "string") {
        const cmp = va.localeCompare(vb as string, "fr");
        return sortDir === "asc" ? cmp : -cmp;
      }
      const cmp = (va as number) - (vb as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, viewMode, sortKey, sortDir]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
        <Bot className="w-6 h-6 animate-pulse" />
        <span className="text-sm">Chargement des robots…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-destructive">
        <p className="font-medium">Erreur de chargement</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/30 rounded-full p-2 transition-colors"
            data-testid="button-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-[90vw] max-h-[80vh] flex flex-col items-center gap-4" onClick={e => e.stopPropagation()}>
            <img
              src={lightbox.imageUrl}
              alt={lightbox.label}
              className="max-w-full max-h-[70vh] object-contain drop-shadow-2xl rounded-lg"
              data-testid="lightbox-image"
            />
            <p className="text-white font-medium text-base text-center px-4">{lightbox.label}</p>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="px-6 pt-6 pb-4 border-b border-border/60 bg-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground" data-testid="text-page-title">Robots actifs</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              {robots.length} robot{robots.length !== 1 ? "s" : ""} synchronisé{robots.length !== 1 ? "s" : ""} depuis Pudu Cloud
            </p>
          </div>
          {/* Toggle vue liste / grille */}
          {!isMobile && (
            <div className="flex items-center gap-1 border border-border rounded-lg p-0.5 flex-shrink-0">
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded-md transition-colors ${viewModePref === "list" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="toggle-robots-list"
                title="Vue liste"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded-md transition-colors ${viewModePref === "grid" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="toggle-robots-grid"
                title="Vue cartes"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Barre de recherche + filtre type */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher par nom, SN, modèle ou magasin…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="input-search-robots"
            />
          </div>
          <div className="relative">
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 w-full sm:w-auto"
              data-testid="select-robot-type"
            >
              <option value="tous">Tous les modèles</option>
              {robotTypes.map(t => {
                const robot = robots.find(r => r.product_code === t || r.robot_type === t);
                const label = robot?.product_name || t;
                return <option key={t} value={t}>{label}</option>;
              })}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-16 space-y-3 p-6">
            <Bot className="w-12 h-12 text-muted-foreground/40 mx-auto" />
            {robots.length === 0 ? (
              <div>
                <p className="font-medium text-foreground">Aucun robot actif trouvé.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Contactez votre responsable Jobotto si vous pensez qu'il s'agit d'une erreur.
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Aucun robot ne correspond à votre recherche.</p>
            )}
          </div>
        ) : viewMode === "list" ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="px-4 py-2.5 w-16"></th>
                  <SortThR label="Nom du modèle" sortK="nom" currentKey={sortKey} currentDir={sortDir} onToggle={toggleSort} />
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">SN</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">MAC</th>
                  <SortThR label="Magasin / Lieu" sortK="magasin" currentKey={sortKey} currentDir={sortDir} onToggle={toggleSort} />
                  <SortThR label="Dernière synchro" sortK="synchro" currentKey={sortKey} currentDir={sortDir} onToggle={toggleSort} right />
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {sortedFiltered.map(robot => (
                  <RobotListRow
                    key={robot.robot_sn}
                    robot={robot}
                    onLightbox={openLightbox}
                    onOpenTicket={handleOpenTicket}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(robot => (
              <div
                key={robot.robot_sn}
                className="bg-card border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                data-testid={`card-robot-${robot.robot_sn}`}
              >
                <div
                  className={`h-40 bg-muted/30 flex items-center justify-center overflow-hidden relative group ${robot.image_url ? "cursor-zoom-in" : ""}`}
                  onClick={() => robot.image_url && openLightbox(robot.image_url, modelLabel(robot))}
                >
                  {robot.image_url ? (
                    <>
                      <img
                        src={robot.image_url}
                        alt={modelLabel(robot)}
                        className="h-full w-full object-contain p-3 transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-90 transition-opacity drop-shadow" />
                      </div>
                    </>
                  ) : (
                    <Bot className="w-16 h-16 text-muted-foreground/30" />
                  )}
                </div>

                <div className="p-4 space-y-3">
                  <div>
                    <p className="font-semibold text-sm text-foreground leading-tight">{modelLabel(robot)}</p>
                    {robot.product_code && (
                      <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                        {robot.product_code}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-[11px] font-mono px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-800">
                        SN&nbsp;{robot.robot_sn}
                      </span>
                    </div>
                    {robot.robot_mac && (
                      <span className="inline-flex items-center gap-1 bg-muted text-muted-foreground text-[11px] font-mono px-2 py-0.5 rounded-full border border-border">
                        {robot.robot_mac}
                      </span>
                    )}
                  </div>

                  {robot.shop_name && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Store className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{robot.shop_name}</span>
                    </div>
                  )}

                  <button
                    onClick={() => handleOpenTicket(robot)}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
                    data-testid={`button-open-ticket-card-${robot.robot_sn}`}
                  >
                    <LifeBuoy className="w-3.5 h-3.5" />
                    Ouvrir un ticket
                  </button>

                  <p className="text-[10px] text-muted-foreground/70 border-t border-border pt-2">
                    Synchronisé le {formatDate(robot.last_sync_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
